<template>
  <span>
    <div class="relative">
      <input
          v-model="displayValue"
          @input="handleInput"
          @blur="formatDate"
          :id="id"
          :placeholder="placeholder"
          :disabled="disabled"
          class="w-full px-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 text-[10px] text-center bg-blue-50/80 select-input text-xs"          dir="rtl"
      />
      <label v-if="label" class="absolute -top-2 left-2 bg-white px-1 text-xs text-gray-500">
        {{ label }}
      </label>

      <button
          :disabled="disabled"
          class="absolute left-2 top-2 text-gray-600 hover:text-gray-800 cursor-pointer"
          @click="$refs.datepicker.visible = true"
          @contextmenu.prevent="setToday"
          title="کلیک: انتخاب | راست کلیک: امروز"
      >
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      </button>

      <!-- دکمه پاک کردن -->
      <button
          v-if="displayValue"
          @click="clearDate"
          class="absolute right-2 top-2 text-gray-400 hover:text-gray-600"
          :disabled="disabled"
          title="پاک کردن"
      >
        ×
      </button>
    </div>

    <vue-persian-datetime-picker
        v-model="displayValue"
        @input="handleDatePickerInput"
        ref="datepicker"
        :element="id"
        :editable="true"
        format="jYYYY/jMM/jDD"
        display-format="jYYYY/jMM/jDD"
    />
  </span>
</template>

<script>
import IMask from "imask";
import VuePersianDatetimePicker from "vue-persian-datetime-picker";
import { toGregorian, toJalaali } from "jalaali-js";

export default {
  name: "SDate",
  props: ["value", "default", "disabled", "placeholder", "label"],
  components: { VuePersianDatetimePicker },
  data() {
    return {
      displayValue: null,
      internalValue: null,
      mask: null,
      isDirty: false,
      id: "date" + (Math.random() * 100000).toFixed(0),
    };
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal !== this.internalValue) {
          this.internalValue = newVal || null;
          this.displayValue = this.convertToJalali(newVal);
        }
      }
    }
  },
  created() {
    // ماسک ورودی دستی کاربر (جلالی)
    this.mask = IMask.createMask({
      mask: "YYYY/MM/DD",
      lazy: true,
      blocks: {
        YYYY: { mask: "0000" },
        MM: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 12,
        },
        DD: {
          mask: IMask.MaskedRange,
          from: 1,
          to: 31,
        },
      },
    });
  },
  methods: {
    //-------------------------------------------------
    // Helper: pad به دو رقم
    pad2(n) {
      n = parseInt(n, 10);
      if (isNaN(n)) return "00";
      return n < 10 ? "0" + n : "" + n;
    },

    //-------------------------------------------------
    // ساخت رشته datetime میلادی برای ارسال به Django
    // ورودی: سال/ماه/روز میلادی
    // خروجی: "YYYY-MM-DD HH:MM:SS" با زمان فعلی سیستم
    buildDateTimeString(gy, gm, gd) {
      const now = new Date();

      const Y = gy;
      const M = this.pad2(gm);
      const D = this.pad2(gd);

      return `${Y}-${M}-${D}`;
    },

    //-------------------------------------------------
    // jalali -> gregorian
    convertJalaliToGregorianDateTime(jalaliDateStr) {
      // jalaliDateStr: "1403/08/10"
      if (!jalaliDateStr) return null;

      const parts = jalaliDateStr.split("/");
      if (parts.length !== 3) return null;

      const [jy, jm, jd] = parts.map(Number);
      if (!jy || !jm || !jd) return null;

      const g = toGregorian(jy, jm, jd); // {gy, gm, gd}
      if (!g || !g.gy) return null;

      return this.buildDateTimeString(g.gy, g.gm, g.gd);
    },

    //-------------------------------------------------
    // gregorian -> jalali فقط برای نمایش
    // می‌گیریم DateTime میلادی مثل "2025-11-01 16:42:07"
    // یا "2025-11-01" و تبدیل می‌کنیم به "1404/08/10"
    convertToJalali(isoOrDateTime) {
      if (!isoOrDateTime) return null;
      try {
        // استخراج بخش تاریخ از ورودی
        // ورودی ممکنه باشه "2025-11-01 16:42:07" یا "2025-11-01"
        const datePart = isoOrDateTime.split(" ")[0]; // "2025-11-01"
        const [gy, gm, gd] = datePart.split("-").map(Number);
        if (!gy || !gm || !gd) return null;

        const j = toJalaali(gy, gm, gd); // {jy, jm, jd}

        return (
            j.jy +
            "/" +
            String(j.jm).padStart(2, "0") +
            "/" +
            String(j.jd).padStart(2, "0")
        );
      } catch (e) {
        console.error("Date conversion error:", e);
        return null;
      }
    },

    handleInput(e) {
      this.isDirty = true;
      this.displayValue = e.target.value;
    },

    // وقتی input فوکوس رو از دست میده:
    // 1. ماسک رو اعمال کن
    // 2. تبدیل کن به میلادی+ساعت فعلی
    // 3. بفرست بیرون
    formatDate() {
      if (!this.displayValue) return;

      // ماسک کن تا فرمت jYYYY/jMM/jDD تمیز بشه
      this.mask.resolve(String(this.displayValue));
      const formattedJalali = this.mask.value; // مثلا "1403/08/10"

      if (formattedJalali) {
        this.displayValue = formattedJalali; // ورودی کاربر رو تمیز نشون بده

        // اینجا خروجی میلادی با ساعت فعلی رو می‌سازیم
        const gregorianDateTime = this.convertJalaliToGregorianDateTime(formattedJalali);

        this.internalValue = gregorianDateTime;
        this.$emit("input", this.internalValue);
      }
    },

    // وقتی از vue-persian-datetime-picker انتخاب می‌کنه
    handleDatePickerInput(jalaliVal) {
      // jalaliVal همون "jYYYY/jMM/jDD"
      this.displayValue = jalaliVal;

      const gregorianDateTime = this.convertJalaliToGregorianDateTime(jalaliVal);

      this.internalValue = gregorianDateTime;
      this.$emit("input", this.internalValue);
    },

    clearDate() {
      this.displayValue = null;
      this.internalValue = null;
      this.isDirty = true;
      this.$emit("input", null);
    },

    setToday() {
      this.isDirty = true;

      const now = new Date();

      const gy = now.getFullYear();
      const gm = now.getMonth() + 1; // 0-based
      const gd = now.getDate();

      const j = toJalaali(gy, gm, gd); // {jy,jm,jd}
      const jalaliDisplay =
          j.jy +
          "/" +
          String(j.jm).padStart(2, "0") +
          "/" +
          String(j.jd).padStart(2, "0");

      this.displayValue = jalaliDisplay;

      const gregorianDateTime = this.buildDateTimeString(gy, gm, gd);

      this.internalValue = gregorianDateTime;
      this.$emit("input", this.internalValue);
    }
  },
};
</script>

<style lang="css">
input {
  direction: ltr;
}
</style>
